import { useCallback, useEffect, useState } from 'react';
import {
  ApiError,
  decommission,
  fetchHub,
  recommission,
  setUpdatePolicy,
  updateNow,
} from './api';
import type { Beat, HubDetail as Detail } from './types';

/** Disk usage over the retained beats. Oldest on the left, so it reads forward. */
function Sparkline({ beats }: { beats: Beat[] }) {
  const points = [...beats].reverse().filter((beat) => beat.systemHealth !== null);

  if (points.length === 0) {
    return <p className="muted">no health reported in the retained history</p>;
  }

  return (
    <div className="spark" title="disk used %, oldest first">
      {points.map((beat, index) => (
        <span
          key={index}
          style={{ height: `${Math.max(1, Math.min(100, beat.systemHealth!.diskUsedPercent))}%` }}
        />
      ))}
    </div>
  );
}

export function HubDetail({ hubId, onBack }: { hubId: string; onBack: () => void }) {
  const [hub, setHub] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setHub(await fetchHub(hubId));
  }, [hubId]);

  useEffect(() => {
    load().catch((caught) => setError(caught instanceof ApiError ? caught.message : String(caught)));
  }, [load]);

  async function act(work: () => Promise<string>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setNotice(await work());
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : String(caught));
      // Reload even on failure: a 409 means someone else moved the hub, and
      // the operator needs to be looking at the new values before retrying.
      await load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  if (!hub) {
    return (
      <div>
        <button onClick={onBack}>← fleet</button>
        {error ? <p className="error">{error}</p> : <p className="muted">loading…</p>}
      </div>
    );
  }

  const expected = {
    updateChannel: hub.updateChannel,
    pinnedReleaseId: hub.pinnedRelease?.id ?? null,
  };

  const changeChannel = (channel: string) => act(async () => {
    await setUpdatePolicy(hubId, { channel }, expected);
    return `channel set to ${channel}`;
  });

  return (
    <div>
      <div className="controls">
        <button onClick={onBack}>← fleet</button>
        <h1 style={{ fontSize: 15, margin: 0 }}>{hub.hubId}</h1>
        <span className={`status-${hub.status}`}>
          {hub.state === 'claimed' ? hub.status : hub.state}
        </span>
      </div>

      {error && <p className="error">{error}</p>}
      {notice && <p className="notice">{notice}</p>}
      {hub.tunnel.state === 'unknown' && (
        <p className="warn-box">
          Tunnel status unavailable — Pangolin did not answer. Everything else on
          this page is read from our own registry and is current. ({hub.tunnel.error})
        </p>
      )}

      <div className="panel">
        <h2>registry</h2>
        <dl className="kv">
          <dt>lifecycle</dt><dd>{hub.state}</dd>
          <dt>home</dt><dd>{hub.homeName ? `${hub.homeName} (#${hub.homeId})` : 'not claimed'}</dd>
          <dt>firmware</dt><dd>{hub.firmwareVersion}</dd>
          <dt>local ip</dt><dd>{hub.localIp}</dd>
          <dt>last seen</dt><dd>{hub.lastSeen ?? 'never'}</dd>
          <dt>registered</dt><dd>{hub.created}</dd>
          <dt>tunnel</dt><dd>{hub.tunnel.state}{hub.tunnel.url ? ` — ${hub.tunnel.url}` : ''}</dd>
          <dt>last update</dt>
          <dd>{hub.lastUpdate ? `${hub.lastUpdate.status} — ${hub.lastUpdate.message} (${hub.lastUpdate.recordedAt})` : '—'}</dd>
          {hub.decommissionedAt && (<><dt>decommissioned</dt><dd>{hub.decommissionedAt}</dd></>)}
        </dl>
      </div>

      <div className="panel">
        <h2>update policy</h2>
        <dl className="kv">
          <dt>channel</dt>
          <dd>{hub.effectiveChannel}{hub.updateChannel === null && <span className="muted"> (following the server default)</span>}</dd>
          <dt>pinned</dt>
          <dd>{hub.pinnedRelease ? `${hub.pinnedRelease.version} (${hub.pinnedRelease.channel})` : '—'}</dd>
          <dt>last served</dt>
          <dd>{hub.selectedRelease ? `${hub.selectedRelease.version} (seq ${hub.selectedRelease.sequence})` : '—'}</dd>
        </dl>

        <div className="controls" style={{ marginTop: 10 }}>
          {['stable', 'beta'].map((channel) => (
            <button
              key={channel}
              disabled={busy || !!hub.decommissionedAt || hub.effectiveChannel === channel}
              onClick={() => {
                // Moving channel drops a selection made in the old one, and the
                // device applies any differing tag — including an older one. The
                // operator has to be told that before they do it, not after.
                const confirmed = window.confirm(
                  `Move ${hub.hubId} to "${channel}"?\n\n`
                  + 'The release it was last served belongs to its current channel and '
                  + 'will stop counting as a floor. If the new channel is behind, the '
                  + 'next check will move this device BACKWARDS — the updater applies '
                  + 'any tag that differs, not just newer ones.',
                );
                if (confirmed) void changeChannel(channel);
              }}
            >
              move to {channel}
            </button>
          ))}
          {hub.pinnedRelease && (
            <button
              disabled={busy}
              onClick={() => act(async () => {
                await setUpdatePolicy(hubId, { pinnedReleaseId: null }, expected);
                return 'pin cleared';
              })}
            >
              clear pin
            </button>
          )}
          <button
            disabled={busy || !!hub.decommissionedAt}
            onClick={() => act(async () => {
              const result = await updateNow(hubId);
              return result.deduplicated
                ? 'an update was already queued for this hub — reused it'
                : 'update queued; it rides the next heartbeat';
            })}
          >
            update now
          </button>
        </div>
      </div>

      <div className="panel">
        <h2>heartbeat history (last {hub.heartbeatHistoryLimit} beats kept)</h2>
        <Sparkline beats={hub.heartbeatHistory} />
        <table style={{ marginTop: 10 }}>
          <thead>
            <tr><th>at</th><th>status</th><th>firmware</th><th>cpu</th><th>mem %</th><th>disk %</th><th>update</th></tr>
          </thead>
          <tbody>
            {hub.heartbeatHistory.slice(0, 20).map((beat, index) => (
              <tr key={index}>
                <td>{beat.recordedAt}</td>
                <td className={`status-${beat.status}`}>{beat.status}</td>
                <td>{beat.firmwareVersion}</td>
                <td>{beat.systemHealth ? beat.systemHealth.cpuLoad1m.toFixed(2) : '—'}</td>
                <td>{beat.systemHealth ? Math.round(beat.systemHealth.memoryUsedPercent) : '—'}</td>
                <td>{beat.systemHealth ? Math.round(beat.systemHealth.diskUsedPercent) : '—'}</td>
                <td className="muted">{beat.updateStatus ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {hub.heartbeatHistory.length === 0 && <p className="muted">no beats recorded yet</p>}
      </div>

      <div className="panel">
        <h2>directives</h2>
        <table>
          <thead>
            <tr><th>verb</th><th>issued</th><th>acknowledged</th><th>state</th></tr>
          </thead>
          <tbody>
            {hub.directives.map((directive) => (
              <tr key={directive.id}>
                <td>{directive.verb}</td>
                <td>{directive.issuedAt}</td>
                <td className="muted">{directive.acknowledgedAt ?? '—'}</td>
                <td>{directive.acknowledgedAt ? 'done' : directive.pending ? 'pending' : 'expired'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {hub.directives.length === 0 && <p className="muted">nothing has been queued for this hub</p>}
      </div>

      <div className="panel">
        <h2>lifecycle</h2>
        {hub.decommissionedAt ? (
          <>
            <p className="muted">
              This hub is locked out: it cannot register, heartbeat or fetch an update
              manifest. Recommissioning only lifts that lock — it does not restore a
              tunnel, and it does not change the channel or pin.
            </p>
            <button
              disabled={busy}
              onClick={() => act(async () => {
                const typed = window.prompt(`Type the hub id to recommission it:\n${hub.hubId}`) ?? '';
                await recommission(hubId, typed);
                return 'hub recommissioned';
              })}
            >
              recommission
            </button>
          </>
        ) : (
          <>
            <p className="muted">
              Decommissioning locks the device out permanently — registration, heartbeat
              and updates are all refused — and then tears down its Pangolin site. The
              lock-out is committed first, so it holds even if the teardown fails.
            </p>
            <button
              className="danger"
              disabled={busy}
              onClick={() => act(async () => {
                const typed = window.prompt(`Type the hub id to decommission it:\n${hub.hubId}`) ?? '';
                await decommission(hubId, typed);
                return 'hub decommissioned';
              })}
            >
              decommission
            </button>
          </>
        )}
      </div>
    </div>
  );
}
